import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import { authApiService } from "../../api/api";
import { BatteryComment, BatteryType } from "../../types/battery.type";
import { formatDate, formatTime } from "../../utils/formatDate";
import { getBatteryBmsDisplayTime } from "../../utils/batteryBmsTime";
import BmsBatteryStatus from "../../components/batteries/BmsBatteryStatus/BmsBatteryStatus";
import DefaultModal from "../../components/modals/DefaultModal/DefaultModal";
import ModalTitle from "../../components/modals/ModalTitle/ModalTitle";
import ModalDeleteButtons from "../../components/modals/forms/buttons/ModalDeletButtons/ModalDeleteButtons";
import { ReactComponent as DeleteIcon } from "../../assets/icons/modal/delete.svg";
import styles from "./styles.module.scss";

const BatteryDetailsPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [battery, setBattery] = useState<BatteryType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [commentFileUrl, setCommentFileUrl] = useState<string | null>(null);
  const [commentFileName, setCommentFileName] = useState<string>("");
  const [isSendingComment, setIsSendingComment] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isRequestingStatus, setIsRequestingStatus] = useState(false);
  const [editName, setEditName] = useState("");
  const [editLastLocation, setEditLastLocation] = useState<
    "terminal" | "warehouse" | "client" | "unknown"
  >("warehouse");
  const [editLastLocationId, setEditLastLocationId] = useState("");
  const [editLastLocationName, setEditLastLocationName] = useState("");
  const [editDeltaVoltage, setEditDeltaVoltage] = useState("");
  const [editNote, setEditNote] = useState("");

  const syncEditForm = (value: BatteryType) => {
    setEditName(value.name || "");
    setEditLastLocation(value.lastLocation || "warehouse");
    setEditLastLocationName(value.lastLocationName || "");
    setEditLastLocationId(value.lastLocationId || "");
    setEditDeltaVoltage(
      typeof value.deltaVoltage === "number" ? String(value.deltaVoltage) : ""
    );
    setEditNote(value.note || "");
  };

  const loadBattery = async (options?: { silent?: boolean }) => {
    if (!id) {
      return;
    }
    if (!options?.silent) {
      setIsLoading(true);
    }
    try {
      const result = await authApiService.getBatteryById(id);
      if (result) {
        setBattery(result);
        syncEditForm(result);
      }
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    void loadBattery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sortedComments = useMemo(() => {
    return [...(battery?.comments || [])].sort((first, second) => {
      return (
        new Date(second.createdAt || 0).getTime() -
        new Date(first.createdAt || 0).getTime()
      );
    });
  }, [battery]);

  const onSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploadingFile(true);
    try {
      const response = await authApiService.uploadCdnImage(formData);
      const url = response?.publicUrl || response?.savedFile?.path || null;
      if (!url) {
        toast.error("Не вдалося завантажити файл");
        return;
      }
      setCommentFileUrl(url);
      setCommentFileName(file.name);
      toast.success("Файл завантажено");
    } catch {
      toast.error("Не вдалося завантажити файл");
    } finally {
      setUploadingFile(false);
    }
  };

  const onSendComment = async () => {
    if (!id || (!commentText.trim() && !commentFileUrl)) {
      return;
    }

    setIsSendingComment(true);
    try {
      const result = await authApiService.addBatteryComment(id, {
        text: commentText.trim(),
        file: commentFileUrl,
      });
      if (result) {
        setBattery(result);
        setCommentText("");
        setCommentFileName("");
        setCommentFileUrl(null);
      }
    } finally {
      setIsSendingComment(false);
    }
  };

  const onSaveBattery = async () => {
    if (!id || !battery) {
      return;
    }

    if (!editName.trim()) {
      toast.error("Назва обов'язкова");
      return;
    }

    if (editDeltaVoltage && Number.isNaN(Number(editDeltaVoltage))) {
      toast.error("Різниця напруги має бути числом");
      return;
    }

    setIsSaving(true);
    try {
      const result = await authApiService.updateBattery(id, {
        name: editName.trim(),
        lastLocation: editLastLocation,
        lastLocationName: editLastLocationName.trim() || null,
        lastLocationId: editLastLocationId.trim() || null,
        deltaVoltage: editDeltaVoltage ? Number(editDeltaVoltage) : null,
        note: editNote.trim() || undefined,
        lastUpdatedAt: new Date(),
      });
      if (result) {
        setBattery(result);
        syncEditForm(result);
        setIsEditMode(false);
        toast.success("Акумулятор оновлено");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Помилка збереження");
    } finally {
      setIsSaving(false);
    }
  };

  const onRequestBatteryStatus = async () => {
    if (!battery?.id || isRequestingStatus) {
      return;
    }

    setIsRequestingStatus(true);
    try {
      const response = await authApiService.requestBatteryStatus(battery.id);
      if (response?.requested) {
        toast.success(`Запит на оновлення стану надіслано (${response.terminalId})`);
      } else {
        toast.error("Не вдалося надіслати запит на термінал");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Помилка надсилання запиту на термінал"
      );
    } finally {
      setIsRequestingStatus(false);
      void loadBattery({ silent: true });
    }
  };

  const onDeleteBattery = async () => {
    if (!id || isDeleting) {
      return;
    }

    setIsDeleting(true);
    try {
      await authApiService.deleteBattery(id);
      toast.success("Акумулятор видалено");
      setIsDeleteConfirmOpen(false);
      navigate("/batteries");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Не вдалося видалити акумулятор"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const renderFile = (comment: BatteryComment) => {
    if (!comment.file) {
      return null;
    }

    const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(comment.file);

    if (isImage) {
      return (
        <a
          href={comment.file}
          target="_blank"
          rel="noreferrer"
          className={styles.commentMediaLink}
        >
          <img className={styles.commentImage} src={comment.file} alt="comment file" />
        </a>
      );
    }

    return (
      <a
        href={comment.file}
        target="_blank"
        rel="noreferrer"
        className={styles.fileLink}
      >
        Відкрити файл
      </a>
    );
  };

  if (isLoading) {
    return <main className={styles.container}>Завантаження...</main>;
  }

  if (!battery) {
    return <main className={styles.container}>Акумулятор не знайдено</main>;
  }

  return (
    <main className={styles.container}>
      <div className={styles.backRow}>
        <Link to="/batteries" className={styles.backLink}>
          ← До списку акумуляторів
        </Link>
        <div className={styles.editActions}>
          {isEditMode ? (
            <>
              <button
                className={styles.dangerButton}
                onClick={() => setIsDeleteConfirmOpen(true)}
                disabled={isDeleting}
              >
                Видалити
              </button>
              <button
                className={styles.secondaryButton}
                onClick={() => {
                  if (battery) {
                    syncEditForm(battery);
                  }
                  setIsEditMode(false);
                }}
              >
                Скасувати
              </button>
              <button
                className={styles.primaryButton}
                onClick={onSaveBattery}
                disabled={isSaving}
              >
                {isSaving ? "Збереження..." : "Зберегти"}
              </button>
            </>
          ) : (
            <button
              className={styles.primaryButton}
              onClick={() => setIsEditMode(true)}
            >
              Редагувати
            </button>
          )}
        </div>
      </div>

      {isDeleteConfirmOpen && (
        <DefaultModal>
          <div className={styles.deleteModal}>
            <ModalTitle
              onClose={() => setIsDeleteConfirmOpen(false)}
              title="Підтвердіть видалення акумулятора"
              subTitle="Ви дійсно хочете видалити цей акумулятор? Цю дію неможливо скасувати."
            >
              <DeleteIcon />
            </ModalTitle>
            <ModalDeleteButtons
              onPressApply={onDeleteBattery}
              onPressBack={() => setIsDeleteConfirmOpen(false)}
              applyTitle={isDeleting ? "Видалення..." : "Видалити"}
              backTitle="Назад"
            />
          </div>
        </DefaultModal>
      )}

      <div className={styles.infoGrid}>
        <div>
          <p className={styles.label}>Назва</p>
          {isEditMode ? (
            <input
              className={styles.editInput}
              value={editName}
              onChange={(event) => setEditName(event.currentTarget.value)}
            />
          ) : (
            <p className={styles.value}>{battery.name || "-"}</p>
          )}
        </div>
        <div>
          <p className={styles.label}>MAC</p>
          <p className={styles.value}>{battery.macAddress || "-"}</p>
        </div>
        <div>
          <p className={styles.label}>Локація</p>
          {isEditMode ? (
            <select
              className={styles.editInput}
              value={editLastLocation}
              onChange={(event) =>
                setEditLastLocation(
                  event.currentTarget.value as
                    | "terminal"
                    | "warehouse"
                    | "client"
                    | "unknown"
                )
              }
            >
              <option value="warehouse">warehouse</option>
              <option value="terminal">terminal</option>
              <option value="client">client</option>
              <option value="unknown">unknown</option>
            </select>
          ) : (
            <p className={styles.value}>{battery.lastLocation || "-"}</p>
          )}
        </div>
        <div>
          <p className={styles.label}>Назва локації</p>
          {isEditMode ? (
            <input
              className={styles.editInput}
              value={editLastLocationName}
              onChange={(event) => setEditLastLocationName(event.currentTarget.value)}
              placeholder="Напр. PT0003 або склад"
            />
          ) : (
            <p className={styles.value}>{battery.lastLocationName?.trim() || "-"}</p>
          )}
        </div>
        <div>
          <p className={styles.label}>ID локації</p>
          {isEditMode ? (
            <input
              className={styles.editInput}
              value={editLastLocationId}
              onChange={(event) => setEditLastLocationId(event.currentTarget.value)}
            />
          ) : (
            <p className={styles.value}>{battery.lastLocationId || "-"}</p>
          )}
        </div>
        <div>
          <p className={styles.label}>Різниця напруги (ΔV)</p>
          {isEditMode ? (
            <input
              className={styles.editInput}
              value={editDeltaVoltage}
              onChange={(event) => setEditDeltaVoltage(event.currentTarget.value)}
            />
          ) : (
            <p className={styles.value}>
              {typeof battery.deltaVoltage === "number"
                ? battery.deltaVoltage.toFixed(3)
                : "-"}
            </p>
          )}
        </div>
        <div>
          <p className={styles.label}>Оновлення BMS</p>
          <p className={styles.value}>
            {(() => {
              const t = getBatteryBmsDisplayTime(battery);
              return t ? `${formatDate(t)} ${formatTime(t)}` : "-";
            })()}
          </p>
        </div>
        <div>
          <p className={styles.label}>Виготовлено</p>
          <p className={styles.value}>
            {battery.madeAt
              ? `${formatDate(battery.madeAt)} ${formatTime(battery.madeAt)}`
              : "-"}
          </p>
        </div>
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Нотатка</h3>
        {isEditMode ? (
          <textarea
            className={styles.noteInput}
            value={editNote}
            onChange={(event) => setEditNote(event.currentTarget.value)}
          />
        ) : (
          <p className={styles.note}>{battery.note || "-"}</p>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.bmsHeader}>
          <h3 className={styles.sectionTitle}>Стан BMS</h3>
          <button
            type="button"
            className={styles.requestStatusButton}
            onClick={onRequestBatteryStatus}
            disabled={isRequestingStatus}
          >
            {isRequestingStatus ? "Оновлення..." : "Оновити стан"}
          </button>
        </div>
        <div className={styles.bmsCard}>
          <BmsBatteryStatus bmsLastRawData={battery.bmsLastRawData} variant="detail" />
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Коментарі</h3>

        <div className={styles.addCommentBox}>
          <textarea
            className={styles.commentInput}
            placeholder="Додайте коментар..."
            value={commentText}
            onChange={(event) => setCommentText(event.currentTarget.value)}
          />
          <div className={styles.commentActions}>
            <label className={styles.uploadButton}>
              {uploadingFile ? "Завантаження..." : "Додати файл/фото"}
              <input type="file" onChange={onSelectFile} hidden />
            </label>
            {commentFileName && <span className={styles.fileName}>{commentFileName}</span>}
            <button
              className={styles.sendButton}
              onClick={onSendComment}
              disabled={isSendingComment || uploadingFile}
            >
              {isSendingComment ? "Надсилання..." : "Надіслати"}
            </button>
          </div>
        </div>

        <div className={styles.commentsList}>
          {sortedComments.length === 0 && <p>Коментарів ще немає</p>}
          {sortedComments.map((comment) => (
            <div className={styles.commentCard} key={comment.id}>
              <div className={styles.commentHeader}>
                <span className={styles.commentAuthor}>
                  {comment.author?.fullName || "Користувач"}
                </span>
                <span className={styles.commentTime}>
                  {comment.createdAt
                    ? `${formatDate(comment.createdAt)} ${formatTime(comment.createdAt)}`
                    : "-"}
                </span>
              </div>
              {comment.text && <p className={styles.commentText}>{comment.text}</p>}
              {renderFile(comment)}
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default BatteryDetailsPage;
